import * as ExcelJS from "exceljs";
import type { CaParPeriodeDialogData } from "./ca-par-periode-dialog.component";
import type { CaPeriodeScale } from "./ca-par-periode-tiers.component";
import {
  ExcelPeriodRow,
  computeTreeForScale,
} from "./ca-par-periode-compute.util";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  periodBg: "FFE8EDF3",
  periodFont: "FF1B2A4A",
  articleBg: "FFF5F7FA",
  lotBg: "FFFFFFFF",
  lotFont: "FF667085",
  border: "FFD0D5DD",
  titleColor: "FF1B2A4A",
  statGreen: "FFE6FFF5",
  statPurple: "FFF0E6FF",
  statOrange: "FFFFF0E0",
  statRed: "FFFEE2E2",
  inputBg: "FFFFFBEB", // inputs utilisateur (PU / coeff) : fond jaune pâle
  matBg: "FFE8FBF3", // rappel colonne matière
  moBg: "FFF3EBFE", // rappel colonne MO
  margeGood: "FF16A34A",
  margeWarn: "FFF59E0B",
  margeBad: "FFDC2626",
};

function colLetter(col: number): string {
  let s = "";
  let c = col;
  while (c > 0) {
    c--;
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26);
  }
  return s;
}

function cellRef(col: number, row: number): string {
  return `${colLetter(col)}${row}`;
}

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  cell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}

function statStyle(cell: ExcelJS.Cell, bg: string) {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

/**
 * Colonnes (16 au total) — organisées en 3 blocs :
 *   Contexte (1-3) : Période | Article | Lot
 *   Inputs unitaires (4-9) : Qté UV | PU vente UV | Coeff UG | Qté UG* | PU coût mat | PU coût MO
 *     (* Qté UG est une formule, mais placée avec les inputs pour la lisibilité)
 *   Valeurs dérivées (10-15) : CA | Mat | MO | Coût | Marge | Taux
 *   Part (16) : % CA
 *
 * Toutes les valeurs € et la quantité UG sont des formules dérivées des
 * inputs : l'utilisateur peut éditer les PU / coeff dans Excel et voir
 * CA, coût et marge se recalculer automatiquement à tous les niveaux.
 */
const COL = {
  period: 1,
  article: 2,
  lot: 3,
  qtyUV: 4,
  puVenteUV: 5,
  coefUG: 6,
  qtyUG: 7,
  puCoutMat: 8,
  puCoutMO: 9,
  ca: 10,
  mat: 11,
  mo: 12,
  cout: 13,
  marge: 14,
  taux: 15,
  pct: 16,
};
const HEADER_ROW = 5;
const COL_WIDTHS = [
  14, 28, 12, // contexte
  10, 12, 9, 10, 13, 13, // inputs
  12, 12, 12, 13, 12, 9, // dérivés
  9, // part
];

export async function exportCaParPeriodeExcel(
  data: CaParPeriodeDialogData,
  articleNameResolver: (articleId: string) => string,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  const sheets: { scale: CaPeriodeScale; label: string }[] = [
    { scale: "MONTH", label: "Mois" },
    { scale: "WEEK", label: "Semaine" },
    { scale: "DAY", label: "Jour" },
  ];

  for (const { scale, label } of sheets) {
    const tree = computeTreeForScale(
      data.facturesLignes,
      data.lotCostMap,
      scale,
      data.articleFilterId,
      articleNameResolver,
    );
    buildSheet(wb, label, tree, data);
    await addJustupBranding(wb, wb.getWorksheet(label)!, 16);
  }

  const buffer = (await wb.xlsx.writeBuffer()) as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const safeName = (data.tiersDenomination || "tiers")
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .slice(0, 40);
  const filename = `ca-periode-${safeName}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 10000);
}

function buildSheet(
  wb: ExcelJS.Workbook,
  sheetName: string,
  tree: ExcelPeriodRow[],
  data: CaParPeriodeDialogData,
) {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: HEADER_ROW, xSplit: 3 }],
  });
  COL_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  // Formats Excel natifs
  // - eurFmt : format monétaire avec 2 décimales (cellules CA / Coût / Marge)
  // - puFmt  : 4 décimales pour les prix unitaires (€/UV et €/UG sont souvent
  //   très petits — sans 4 décimales, 0,3 s'affiche parfois en "0,30" voire
  //   "0" si coincé avec "#,##0").
  // - qtyFmt : 2 décimales pour les quantités
  // - coefFmt : 4 décimales pour le coefficient UG/UV
  const eurFmt = '#,##0.00\\ "€"';
  const puFmt = '#,##0.0000\\ "€"';
  const qtyFmt = "#,##0.00";
  const coefFmt = "#,##0.0000";

  // ── Titre (laisse la dernière colonne pour le branding JustUp)
  ws.mergeCells("A1:O1");
  const title = ws.getCell("A1");
  title.value = `CA par ${sheetName.toLowerCase()} — ${data.tiersDenomination}`;
  title.font = { bold: true, size: 16, color: { argb: C.titleColor } };
  title.alignment = { vertical: "middle" };
  ws.getRow(1).height = 30;

  if (data.articleFilterName) {
    ws.mergeCells("A2:P2");
    const sub = ws.getCell("A2");
    sub.value = `Article filtré : ${data.articleFilterName}`;
    sub.font = { italic: true, size: 10, color: { argb: "FF666666" } };
  }

  // ── Stats
  ws.mergeCells("A3:D3");
  ws.mergeCells("E3:I3");
  ws.mergeCells("J3:P3");
  const s1 = ws.getCell("A3");
  s1.value = `CA : ${formatEur(data.totalCA)}`;
  statStyle(s1, C.statGreen);
  const s2 = ws.getCell("E3");
  s2.value = `Mat. ${formatEur(data.totalCostMat)} + MO ${formatEur(data.totalCostMO)} = ${formatEur(data.totalCost)}`;
  statStyle(s2, C.statOrange);
  const s3 = ws.getCell("J3");
  s3.value = `Marge : ${formatEur(data.totalMargin)} (${(data.totalMarginPct * 100).toFixed(1)}%)`;
  statStyle(
    s3,
    data.totalMarginPct >= 0.35
      ? C.statGreen
      : data.totalMarginPct >= 0.2
        ? C.statOrange
        : C.statRed,
  );
  ws.getRow(3).height = 28;

  // ── Headers (2 lignes : bloc titré + intitulé détaillé)
  // Pour rester simple, une seule ligne header mais avec wrap-text.
  const HEADERS = [
    sheetName,
    "Article",
    "N° Lot",
    "Qté UV",
    "PU vente (€/UV)",
    "Coeff UG/UV",
    "Qté UG",
    "PU coût mat. (€/UG)",
    "PU coût MO (€/UG)",
    "CA (€)",
    "Matière (€)",
    "MO (€)",
    "Coût total (€)",
    "Marge (€)",
    "Taux",
    "% CA",
  ];
  HEADERS.forEach((h, i) => {
    const c = ws.getCell(HEADER_ROW, i + 1);
    c.value = h;
    headerStyle(c);
  });
  // Rappel colonnes mat/mo : petite bande colorée en bas des entêtes
  ws.getCell(HEADER_ROW, COL.mat).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  ws.getCell(HEADER_ROW, COL.mo).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  ws.getRow(HEADER_ROW).height = 34;
  ws.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: HEADER_ROW, column: 16 },
  };

  // ── Lignes : Période → Article → Lot
  let row = HEADER_ROW + 1;
  const periodRowNumbers: number[] = [];

  for (const period of tree) {
    const periodRowNum = row;
    periodRowNumbers.push(periodRowNum);
    row++;

    const articleRowNumbers: number[] = [];

    for (const art of period.articles) {
      const articleRowNum = row;
      articleRowNumbers.push(articleRowNum);
      row++;

      const lotRowNumbers: number[] = [];

      for (const lot of art.lots) {
        const r = ws.getRow(row);

        // Contexte
        r.getCell(COL.lot).value = lot.lotNumero || "—";

        // ── Inputs utilisateur (éditables dans Excel) ──
        r.getCell(COL.qtyUV).value = lot.qty;
        r.getCell(COL.puVenteUV).value = lot.prixUnitaire;
        r.getCell(COL.coefUG).value = lot.coefUG;
        r.getCell(COL.puCoutMat).value = lot.puCoutMat;
        r.getCell(COL.puCoutMO).value = lot.puCoutMO;

        // ── Formules dérivées ──
        r.getCell(COL.qtyUG).value = {
          formula: `${cellRef(COL.qtyUV, row)}*${cellRef(COL.coefUG, row)}`,
          result: lot.qtyUG,
        } as any;
        r.getCell(COL.ca).value = {
          formula: `${cellRef(COL.qtyUV, row)}*${cellRef(COL.puVenteUV, row)}`,
          result: lot.ca,
        } as any;
        r.getCell(COL.mat).value = {
          formula: `${cellRef(COL.qtyUG, row)}*${cellRef(COL.puCoutMat, row)}`,
          result: lot.costMat,
        } as any;
        r.getCell(COL.mo).value = {
          formula: `${cellRef(COL.qtyUG, row)}*${cellRef(COL.puCoutMO, row)}`,
          result: lot.costMO,
        } as any;
        r.getCell(COL.cout).value = {
          formula: `${cellRef(COL.mat, row)}+${cellRef(COL.mo, row)}`,
          result: lot.costMat + lot.costMO,
        } as any;
        r.getCell(COL.marge).value = {
          formula: `${cellRef(COL.ca, row)}-${cellRef(COL.cout, row)}`,
          result: lot.ca - lot.costMat - lot.costMO,
        } as any;
        r.getCell(COL.taux).value = {
          formula: `IF(${cellRef(COL.ca, row)}=0,0,${cellRef(COL.marge, row)}/${cellRef(COL.ca, row)})`,
          result: 0,
        } as any;

        styleLotRow(r);
        // Formats numériques (PU en 4 décimales pour gérer les fractions)
        r.getCell(COL.qtyUV).numFmt = qtyFmt;
        r.getCell(COL.qtyUG).numFmt = qtyFmt;
        r.getCell(COL.coefUG).numFmt = coefFmt;
        r.getCell(COL.puVenteUV).numFmt = puFmt;
        r.getCell(COL.puCoutMat).numFmt = puFmt;
        r.getCell(COL.puCoutMO).numFmt = puFmt;
        [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach(
          (c) => (r.getCell(c).numFmt = eurFmt),
        );
        r.getCell(COL.taux).numFmt = "0.0%";
        // Fonds rappel inputs / mat / mo
        [COL.qtyUV, COL.puVenteUV, COL.coefUG, COL.puCoutMat, COL.puCoutMO].forEach(
          (c) => {
            r.getCell(c).fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: C.inputBg },
            };
          },
        );
        r.getCell(COL.mat).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: C.matBg },
        };
        r.getCell(COL.mo).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: C.moBg },
        };

        lotRowNumbers.push(row);
        row++;
      }

      // ── Ligne Article (SUM des lots) ──
      const ar = ws.getRow(articleRowNum);
      ar.getCell(COL.article).value = art.articleName;

      const sumIf = (col: number) =>
        lotRowNumbers.length
          ? ({
              formula: `SUM(${lotRowNumbers.map((rn) => cellRef(col, rn)).join(",")})`,
              result: 0,
            } as any)
          : 0;
      ar.getCell(COL.qtyUV).value = sumIf(COL.qtyUV);
      ar.getCell(COL.qtyUG).value = sumIf(COL.qtyUG);
      ar.getCell(COL.ca).value = sumIf(COL.ca);
      ar.getCell(COL.mat).value = sumIf(COL.mat);
      ar.getCell(COL.mo).value = sumIf(COL.mo);
      ar.getCell(COL.cout).value = {
        formula: `${cellRef(COL.mat, articleRowNum)}+${cellRef(COL.mo, articleRowNum)}`,
        result: 0,
      } as any;
      ar.getCell(COL.marge).value = {
        formula: `${cellRef(COL.ca, articleRowNum)}-${cellRef(COL.cout, articleRowNum)}`,
        result: 0,
      } as any;
      ar.getCell(COL.taux).value = {
        formula: `IF(${cellRef(COL.ca, articleRowNum)}=0,0,${cellRef(COL.marge, articleRowNum)}/${cellRef(COL.ca, articleRowNum)})`,
        result: 0,
      } as any;

      styleArticleRow(ar);
      ar.getCell(COL.qtyUV).numFmt = qtyFmt;
      ar.getCell(COL.qtyUG).numFmt = qtyFmt;
      [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach(
        (c) => (ar.getCell(c).numFmt = eurFmt),
      );
      ar.getCell(COL.taux).numFmt = "0.0%";
    }

    // ── Ligne Période (SUM des articles) ──
    const pr = ws.getRow(periodRowNum);
    pr.getCell(COL.period).value = period.label;

    const sumIf = (col: number) =>
      articleRowNumbers.length
        ? ({
            formula: `SUM(${articleRowNumbers.map((rn) => cellRef(col, rn)).join(",")})`,
            result: 0,
          } as any)
        : 0;
    pr.getCell(COL.qtyUV).value = sumIf(COL.qtyUV);
    pr.getCell(COL.qtyUG).value = sumIf(COL.qtyUG);
    pr.getCell(COL.ca).value = sumIf(COL.ca);
    pr.getCell(COL.mat).value = sumIf(COL.mat);
    pr.getCell(COL.mo).value = sumIf(COL.mo);
    pr.getCell(COL.cout).value = {
      formula: `${cellRef(COL.mat, periodRowNum)}+${cellRef(COL.mo, periodRowNum)}`,
      result: 0,
    } as any;
    pr.getCell(COL.marge).value = {
      formula: `${cellRef(COL.ca, periodRowNum)}-${cellRef(COL.cout, periodRowNum)}`,
      result: 0,
    } as any;
    pr.getCell(COL.taux).value = {
      formula: `IF(${cellRef(COL.ca, periodRowNum)}=0,0,${cellRef(COL.marge, periodRowNum)}/${cellRef(COL.ca, periodRowNum)})`,
      result: 0,
    } as any;

    stylePeriodRow(pr);
    pr.getCell(COL.qtyUV).numFmt = qtyFmt;
    pr.getCell(COL.qtyUG).numFmt = qtyFmt;
    [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach(
      (c) => (pr.getCell(c).numFmt = eurFmt),
    );
    pr.getCell(COL.taux).numFmt = "0.0%";
  }

  // ── Grand total ──
  row++;
  const gtRow = row;
  ws.mergeCells(gtRow, COL.period, gtRow, COL.qtyUV - 1);
  const gtLabel = ws.getCell(gtRow, COL.period);
  gtLabel.value = "TOTAL GÉNÉRAL";
  gtLabel.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtLabel.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  gtLabel.alignment = { horizontal: "right", vertical: "middle" };

  const sumRefs = (col: number) =>
    periodRowNumbers.map((r) => cellRef(col, r)).join(",");
  const totalCols = [
    COL.qtyUV,
    COL.qtyUG,
    COL.ca,
    COL.mat,
    COL.mo,
  ];
  for (const col of totalCols) {
    const cell = ws.getCell(gtRow, col);
    cell.value = periodRowNumbers.length
      ? ({ formula: `SUM(${sumRefs(col)})`, result: 0 } as any)
      : 0;
  }
  ws.getCell(gtRow, COL.cout).value = {
    formula: `${cellRef(COL.mat, gtRow)}+${cellRef(COL.mo, gtRow)}`,
    result: data.totalCost,
  } as any;
  ws.getCell(gtRow, COL.marge).value = {
    formula: `${cellRef(COL.ca, gtRow)}-${cellRef(COL.cout, gtRow)}`,
    result: data.totalMargin,
  } as any;
  ws.getCell(gtRow, COL.taux).value = {
    formula: `IF(${cellRef(COL.ca, gtRow)}=0,0,${cellRef(COL.marge, gtRow)}/${cellRef(COL.ca, gtRow)})`,
    result: data.totalMarginPct,
  } as any;
  ws.getCell(gtRow, COL.pct).value = 1;

  for (let c = COL.qtyUV; c <= COL.pct; c++) {
    ws.getCell(gtRow, c).font = {
      bold: true,
      size: 11,
      color: { argb: C.headerFont },
    };
    ws.getCell(gtRow, c).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerBg },
    };
  }
  ws.getCell(gtRow, COL.qtyUV).numFmt = qtyFmt;
  ws.getCell(gtRow, COL.qtyUG).numFmt = qtyFmt;
  [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach(
    (c) => (ws.getCell(gtRow, c).numFmt = eurFmt),
  );
  ws.getCell(gtRow, COL.taux).numFmt = "0.0%";
  ws.getCell(gtRow, COL.pct).numFmt = "0.0%";
  ws.getRow(gtRow).height = 26;

  // ── % CA sur les lignes période (référence au total) ──
  const gtCaRef = cellRef(COL.ca, gtRow);
  for (const r of periodRowNumbers) {
    ws.getCell(r, COL.pct).value = {
      formula: `IF(${gtCaRef}=0,0,${cellRef(COL.ca, r)}/${gtCaRef})`,
      result: 0,
    } as any;
    ws.getCell(r, COL.pct).numFmt = "0.0%";
    ws.getCell(r, COL.pct).font = {
      bold: true,
      size: 10,
      color: { argb: C.periodFont },
    };
    ws.getCell(r, COL.pct).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.periodBg },
    };
  }

  // ── Mise en forme conditionnelle sur le Taux ──
  ws.addConditionalFormatting({
    ref: `${colLetter(COL.taux)}${HEADER_ROW + 1}:${colLetter(COL.taux)}${row}`,
    rules: [
      {
        type: "cellIs",
        operator: "greaterThan" as any,
        formulae: ["0.349"],
        priority: 1,
        style: { font: { color: { argb: C.margeGood }, bold: true } },
      },
      {
        type: "cellIs",
        operator: "greaterThan" as any,
        formulae: ["0.199"],
        priority: 2,
        style: { font: { color: { argb: C.margeWarn }, bold: true } },
      },
      {
        type: "cellIs",
        operator: "lessThan" as any,
        formulae: ["0.20"],
        priority: 3,
        style: { font: { color: { argb: C.margeBad }, bold: true } },
      },
    ],
  });
}

function styleLotRow(row: ExcelJS.Row) {
  for (let c = 1; c <= 16; c++) {
    const cell = row.getCell(c);
    cell.font = { size: 9, color: { argb: C.lotFont } };
    cell.border = { bottom: { style: "hair", color: { argb: C.border } } };
    if (!cell.fill) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.lotBg },
      };
    }
  }
}

function styleArticleRow(row: ExcelJS.Row) {
  for (let c = 1; c <= 16; c++) {
    const cell = row.getCell(c);
    cell.font = { size: 9, italic: true, color: { argb: "FF334155" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.articleBg },
    };
    cell.border = { bottom: { style: "hair", color: { argb: C.border } } };
  }
}

function stylePeriodRow(row: ExcelJS.Row) {
  for (let c = 1; c <= 16; c++) {
    const cell = row.getCell(c);
    cell.font = { bold: true, size: 10, color: { argb: C.periodFont } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.periodBg },
    };
    cell.border = {
      top: { style: "thin", color: { argb: C.border } },
      bottom: { style: "thin", color: { argb: C.border } },
    };
  }
}

function formatEur(n: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}
